import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { ContractPage, DocumentViewType, ReceivedDataType, ReleaseTab } from '../../../types/contractTable';
import { ActionType } from '../common-component/ConfirmContractActionModal';

export type CompanyDetailsType = {
    id: string;
    name: string;
    phone?: string;
    email?: string;
    website?: string;
    industry?: string | null;
    cityDistrict?: string;
    state?: string;
    country?: string;
    avatarUrl?: string;
    contractComplianceQuantity?: number | null;
    contractViolationQuantity?: number | null;
    rating?: { value: number };
    partnerRating?: number | null;
};

export type OfferFormDataType = {
    file?: File | null;
    emailSubject?: string;
    emailBody?: string;
    automaticReminder?: number;
    recaptcha?: string;
    recieverCompanyId?: string;
    reviseContractId?: string;
};

export type DigioDataType = { agreementId: string; digioId: string; senderIdentifier: string };

export type LazyTableState = {
    first: number;
    rows: number;
    page: number;
    sortField?: string;
    sortOrder?: 1 | -1 | null;
    filters: Record<string, any>;
};

type ReleaseContextType = {
    companyDetails: CompanyDetailsType | null;
    setCompanyDetails: (data: CompanyDetailsType | null) => void;

    contractForm: OfferFormDataType | null;
    setContractForm: (
        data: OfferFormDataType | null | ((prev: OfferFormDataType | null) => OfferFormDataType | null)
    ) => void;

    activePage: ReleaseTab;
    setActivePage: (page: ReleaseTab) => void;

    step: number;
    setStep: (step: number) => void;

    retractReason: string;
    setRetractReason: (reason: string) => void;
    clearRetractReason: () => void;

    digioData: DigioDataType | null;
    setDigioData: (key: DigioDataType | null) => void;

    showModal: boolean;
    setShowModal: (show: boolean) => void;

    loadingAccept: boolean;
    setLoadingAccept: (show: boolean) => void;

    showRetractContractModal: boolean;
    setShowRetractContractModal: (show: boolean) => void;

    showReviewModal: boolean;
    setShowReviewModal: (show: boolean) => void;

    currentPageName: ContractPage;
    setCurrentPageName: (key: ContractPage) => void;

    documentType: DocumentViewType;
    setDocumentType: (key: DocumentViewType) => void;

    reviewData: ReceivedDataType | null;
    setReviewData: (key: any) => void;

    showDeclineContractModal: boolean;
    setShowDeclineContractModal: (show: boolean) => void;

    declineReason: string;
    setDeclineReason: (reason: string) => void;
    clearDeclineReason: () => void;

    resetAllState: () => void;
    lazyState: LazyTableState;
    setLazyState: (state: LazyTableState) => void;
    refreshTable: () => void;

    showSuccessPopUp: boolean;
    setShowSuccessPopUp: (key: boolean) => void;

    successPopUpType: ActionType | null;
    setSuccessPopUpType: (key: ActionType | null) => void;
};

const ReleaseContext = createContext<ReleaseContextType | undefined>(undefined);

export const ReleaseContractProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [companyDetails, setCompanyDetails] = useState<CompanyDetailsType | null>(null);
    const [contractForm, setContractForm] = useState<OfferFormDataType | null>(null);
    const [currentPageName, setCurrentPageName] = useState<ContractPage>(ContractPage.RELEASED);
    const [activePage, setActivePage] = useState<ReleaseTab>(ReleaseTab.SEARCH_PARTNER);
    const [step, setStep] = useState<number>(1);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [documentType, setDocumentType] = useState<DocumentViewType>(DocumentViewType.VIEW_OFFER);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewData, setReviewData] = useState<ReceivedDataType | null>(null);
    const [digioData, setDigioData] = useState<DigioDataType | null>(null);
    const [showRetractContractModal, setShowRetractContractModal] = useState<boolean>(false);
    const [retractReason, setRetractReason] = useState<string>('');
    const [loadingAccept, setLoadingAccept] = useState(false);
    const [showDeclineContractModal, setShowDeclineContractModal] = useState<boolean>(false);
    const [declineReason, setDeclineReason] = useState<string>('');
    const [showSuccessPopUp, setShowSuccessPopUp] = useState(false);
    const [successPopUpType, setSuccessPopUpType] = useState<ActionType | null>(null);
    const [lazyState, setLazyState] = useState<LazyTableState>({
        first: 0,
        rows: 10,
        page: 0,
        sortField: 'releasedDateString',
        sortOrder: -1,
        filters: {},
    });

    const clearRetractReason = useCallback(() => {
        setRetractReason('');
    }, []);

    const clearDeclineReason = useCallback(() => {
        setDeclineReason('');
    }, []);

    const refreshTable = useCallback(() => {
        setLazyState((prevState) => ({
            ...prevState,
            first: 0,
            page: 1,
        }));
    }, []);

    const resetAllState = useCallback(() => {
        setCompanyDetails(null);
        setContractForm(null);
        setCurrentPageName(ContractPage.RELEASED);
        setActivePage(ReleaseTab.SEARCH_PARTNER);
        setStep(1);
        setShowModal(false);
        setDocumentType(DocumentViewType.VIEW_OFFER);
        setShowReviewModal(false);
        setReviewData(null);
        setDigioData(null);
        setShowRetractContractModal(false);
        setRetractReason('');
        setShowDeclineContractModal(false);
        setDeclineReason('');
        setLoadingAccept(false);
    }, []);

    return (
        <ReleaseContext.Provider
            value={{
                companyDetails,
                setCompanyDetails,
                contractForm,
                currentPageName,
                setCurrentPageName,
                setContractForm,
                activePage,
                setActivePage,
                step,
                documentType,
                setDocumentType,
                setStep,
                showModal,
                setShowModal,
                setShowReviewModal,
                showReviewModal,
                reviewData,
                setReviewData,
                setDigioData,
                digioData,
                showRetractContractModal,
                setShowRetractContractModal,
                retractReason,
                setRetractReason,
                clearRetractReason,
                showDeclineContractModal,
                setShowDeclineContractModal,
                declineReason,
                setDeclineReason,
                clearDeclineReason,
                resetAllState,
                loadingAccept,
                setLoadingAccept,
                lazyState,
                setLazyState,
                refreshTable,
                setShowSuccessPopUp,
                setSuccessPopUpType,
                showSuccessPopUp,
                successPopUpType,
            }}
        >
            {children}
        </ReleaseContext.Provider>
    );
};

export const useRelease = () => {
    const ctx = useContext(ReleaseContext);
    if (!ctx) throw new Error('useRelease must be used inside ReleaseContractProvider');
    return ctx;
};
